import { useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { FLOATING_PANEL_HOST_ID } from '@features/notifications/lib/floatingPanelHost';
import {
  anchorRect,
  cardPosition,
  CHIP_GROUP_ATTR,
  cardStrings,
  closesOnEscape,
  isInsideMrsHoverCard,
  MRS_HOVER_CARD_ATTR,
  MRS_HOVER_CARD_ID,
  requestedBeforeLabel,
  type CardPosition,
} from './mrsHoverCard';
import { useMrsHoverCardStore } from './mrsHoverCardStore';

/**
 * PT-7 B1 — mounted ONCE per page (the detail page and the landing). It portals the one card into the content
 * column (`#app-content-column`), which is `relative` and does not itself scroll, so the card's position is the
 * rect difference with no scroll offset. Outside `AppLayout` there is no host, and no card.
 *
 * The card is in the DOM only while open (the template's `sc-if`, L1108). It closes on Escape, on any scroll
 * inside the host (it would detach from its chip otherwise), 120 ms after the pointer leaves chip and card, and
 * when its chip leaves the DOM.
 */
export function MrsHoverCardHost() {
  // Resolved in an effect, never in render: on the first pass AppLayout may not have committed its column
  // yet, and reading it in render would cache the miss (the `useWorkspaceHost` rule).
  const [host, setHost] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setHost(document.getElementById(FLOATING_PANEL_HOST_ID));
  }, []);

  // On unmount: close the card and clear this page's cache.
  useEffect(() => () => useMrsHoverCardStore.getState().reset(), []);

  const openChip = useMrsHoverCardStore((s) => s.openChip);
  const entry = useMrsHoverCardStore((s) => (s.openChip ? s.cache[s.openChip.info.picklistId] : undefined));

  const [position, setPosition] = useState<CardPosition | null>(null);

  useLayoutEffect(() => {
    if (!openChip || !host) {
      setPosition(null);
      return;
    }
    // A chip that left the DOM (its row filtered away under the card) closes the card.
    if (!openChip.element.isConnected) {
      useMrsHoverCardStore.getState().close();
      return;
    }
    // Placed against the chip's GROUP when it has one, so the card never covers a sibling chip (CHIP_GROUP_ATTR).
    const group = openChip.element.closest(`[${CHIP_GROUP_ATTR}]`);
    const anchor = anchorRect(openChip.element.getBoundingClientRect(), group?.getBoundingClientRect() ?? null);
    setPosition(cardPosition(anchor, host.getBoundingClientRect()));
  }, [openChip, host]);

  useEffect(() => {
    if (!openChip || !host) return;
    const close = () => useMrsHoverCardStore.getState().close();
    // BUBBLE phase, and no `defaultPrevented` check: PT-5's dialog prevents the default on this Escape to keep
    // itself open, and the card must still close (`closesOnEscape`).
    const onKeyDown = (e: KeyboardEvent) => {
      if (closesOnEscape(e)) close();
    };
    // A press anywhere but on the card closes it (owner 2026-10-07, e2e 20): a card opened from a chip the pointer
    // happened to rest on otherwise outlived the click that opened a dialog, and sat ABOVE it (z-60 over the dialog's
    // z-50), covering the dialog's own buttons. CAPTURE phase, so a press the dialog or a row swallows still counts.
    const onPointerDown = (e: PointerEvent) => {
      if (!isInsideMrsHoverCard(e.target)) close();
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown, true);
    host.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown, true);
      host.removeEventListener('scroll', close, true);
    };
  }, [openChip, host]);

  if (!host || !openChip || !position) return null;

  const { info } = openChip;
  const summary = entry?.summaries.find((s) => s.materialRequestId === info.materialRequestId);
  const strings = summary ? cardStrings(summary, info.mrsNumber) : null;
  const loading = !strings && (entry === undefined || entry.status === 'loading');
  const requestor = info.requestorFullName || summary?.requestorFullName || null;
  const department = info.departmentName || '—';

  return createPortal(
    <div
      id={MRS_HOVER_CARD_ID}
      role="tooltip"
      {...{ [MRS_HOVER_CARD_ATTR]: '' }}
      onPointerEnter={() => useMrsHoverCardStore.getState().cancelClose()}
      onPointerLeave={() => useMrsHoverCardStore.getState().scheduleClose()}
      style={{ left: position.left, top: position.top, transform: position.transform }}
      className="mrs-hover-card pointer-events-auto absolute z-[60] box-border w-[300px] rounded-[10px] border border-border bg-card p-3 text-foreground"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[12.5px] font-semibold text-info">{info.mrsNumber}</span>
        <span className="text-[11px] text-muted-foreground">
          Requested before {requestedBeforeLabel(info.picklistCreatedAt)}
        </span>
      </div>

      <div className="mt-1.5 grid grid-cols-[auto_minmax(0,1fr)] gap-x-2.5 gap-y-[3px] text-xs">
        <span className="text-muted-foreground">Requestor</span>
        {requestor ? (
          <span className="truncate font-medium" title={requestor}>{requestor}</span>
        ) : loading ? (
          <Skeleton className="h-3.5 w-32" />
        ) : (
          <span>—</span>
        )}
        <span className="text-muted-foreground">Department</span>
        <span className="truncate" title={department === '—' ? undefined : department}>{department}</span>
      </div>

      <div className="mt-2.5 border-t border-border pt-2.5">
        {strings ? (
          <>
            <div className="flex items-baseline justify-between gap-2 text-xs">
              <span className="font-semibold">MRS fulfillment</span>
              <span className="tabular-nums">
                <b className="font-semibold">{strings.pct}%</b>
                <span className="text-muted-foreground"> · {strings.mrsSum} issued</span>
              </span>
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                {/* `role="img"`, not focusable — as `ProgressBar`: focus never moves into the card, so blur on
                    the chip can close it. */}
                <div
                  role="img"
                  aria-label={strings.mrsBarTip}
                  className="mt-1.5 flex h-1.5 cursor-help overflow-hidden rounded-full bg-muted"
                >
                  <span className="h-full bg-success" style={{ width: `${Math.min(strings.pct, 100)}%` }} />
                  <span className="h-full bg-info" style={{ width: `${Math.max(strings.pickPct, 0)}%` }} />
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">{strings.mrsBarTip}</TooltipContent>
            </Tooltip>
            <div className="mt-[7px] flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
              <span className="inline-flex items-center gap-[5px]">
                <span className="h-2 w-2 rounded-[2px] bg-success" aria-hidden />
                {strings.issuedLeg}
              </span>
              {strings.pickLeg && (
                <span className="inline-flex items-center gap-[5px]">
                  <span className="h-2 w-2 rounded-[2px] bg-info" aria-hidden />
                  {strings.pickLeg}
                </span>
              )}
            </div>
            <div className="mt-1 text-[11px] text-muted-foreground">{strings.mrsLines}</div>
          </>
        ) : loading ? (
          <div className="space-y-2" aria-label="Loading MRS figures">
            <Skeleton className="h-3.5 w-full" />
            <Skeleton className="h-1.5 w-full" />
            <Skeleton className="h-3 w-3/4" />
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">MRS figures · could not load</p>
        )}
      </div>

      <div className="mt-2.5 text-[11px] text-muted-foreground">Click the chip to open the MRS in a new tab</div>
    </div>,
    host,
  );
}
